import React, { useEffect, useState, useRef } from "react";
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/backend/php';

// Helper to resolve advisor photo URL properly without double slashes
// Note: Frontend now avoids handling blobs/base64/object URLs — it always prefers backend endpoints
function resolvePhotoUrl(advisor, apiBase, cacheBustTimestamp) {
  if (!advisor || !advisor.id) return null;
  const base = apiBase.replace(/\/+$/, '');

  // Always fetch from database via API endpoint
  return `${base}/api/advisors/${advisor.id}/photo?t=${cacheBustTimestamp}`;
}

function AdvisorsPage() {
  const [advisorsData, setAdvisorsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = React.useRef(false);
  const cacheBustTimestamp = React.useRef(Date.now());
  
  const advisors = advisorsData || [];
  const [imgErrors, setImgErrors] = useState([]);
  const [validImages, setValidImages] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Fetch advisors data without caching
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchAdvisors = async () => {
      const startTime = Date.now();
      try {
        console.log('Starting to fetch advisors...');
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/advisors`);
        console.log('Advisors fetched:', response.data?.length || 0, 'items');
        setAdvisorsData(response.data || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching advisors:', err);
        setError(err.message);
      } finally {
        setLoading(false);
        console.log('Loading completed');
      }
    };

    fetchAdvisors();
  }, []);

  // Reset image errors when advisors or search changes
  useEffect(() => {
    setImgErrors([]);
  }, [searchTerm, advisors]);

  // Image validity is determined by <img> load/error handlers to avoid CORS/HEAD requests

  // Filter advisors by search term
  const filteredAdvisors = advisors.filter(advisor => {
    const name = advisor.name || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get top advisors (those with is_top_advisor = 1)
  const topAdvisors = React.useMemo(() => {
    return advisors.filter(advisor => advisor.is_top_advisor === 1 || advisor.is_top_advisor === true);
  }, [advisors]);

  // Group filtered advisors by area of focus
  const groupedAdvisors = React.useMemo(() => {
    if (!Array.isArray(filteredAdvisors) || filteredAdvisors.length === 0) {
      return {};
    }
    return filteredAdvisors.reduce((groups, advisor) => {
      const area = advisor.area_of_focus || 'General Advisory';
      if (!groups[area]) {
        groups[area] = [];
      }
      groups[area].push(advisor);
      return groups;
    }, {});
  }, [filteredAdvisors]);

  const getInitials = (name) => {
    if (!name) return "A";
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 20
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    hover: {
      y: -5,
      transition: {
        duration: 0.2
      }
    }
  };

  const glowVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 0.3,
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div
      className="w-full max-w-8xl rounded-2xl shadow-strong p-2 sm:p-4 md:p-8 text-sm sm:text-base overflow-x-auto"
      style={{
        background: 'rgba(243, 249, 255, 0.9)', // Light slate background
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: '0 10px 40px rgba(15, 23, 42, 0.15)', // Strong professional shadow
      }}
    >
      <div className="min-h-screen relative overflow-hidden">
        {/* Dynamic Gradient Background */}
        <div className="fixed inset-0 bg-slate-50">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 via-transparent to-transparent"></div>
        </div>

        {/* Animated Background Elements - Simplified */}
        <div className="fixed inset-0 overflow-hidden">
          {/* Static Grid Pattern Only */}
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="h-full w-full" style={{
              backgroundImage: `
                linear-gradient(rgba(71, 85, 105, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(71, 85, 105, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}></div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto">
            
            {/* Header Section */}
            <motion.div 
              className="text-center mb-12 lg:mb-20"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Animated Icon */}
              <motion.div
                className="relative inline-block mb-8"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {/* Pulsing Ring */}
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-blue-400/30"
                  animate={{ scale: 1, opacity: 0.3 }}
                  transition={{ duration: 0.4 }}
                />
              </motion.div>
              
              {/* Title */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-slate-900 mb-6 tracking-tight leading-tight">
                  Our Distinguished
                  <span className="block text-primary-500 mt-2">
                    Advisors
                  </span>
                </h1>
              </motion.div>
              
              <motion.p 
                className="text-lg sm:text-xl lg:text-2xl text-slate-700 max-w-4xl mx-auto leading-relaxed font-light px-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
              >
                Meet our distinguished advisors who guide us with their expertise and insights
              </motion.p>
              
              
              </motion.div>

            {/* Enhanced Search Section */}
            <motion.div 
              className="max-w-lg mx-auto mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.3 }}
            >
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search our amazing advisors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  className="w-full px-8 py-5 bg-white/80 backdrop-blur-xl border border-slate-300/40 rounded-3xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all duration-300 text-lg"
                />
                
                <motion.div 
                  className="absolute right-6 top-1/2 transform -translate-y-1/2"
                  animate={{ rotate: searchTerm ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </motion.div>
              </div>
            </motion.div>

            {/* Advisors Content */}
            <motion.div
              className="relative"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Glassmorphic Container */}
              <div className="relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-6 sm:p-8 lg:p-12 overflow-hidden">
                
                {/* Container Glow Effect */}
                <motion.div
                  className="absolute -inset-1 bg-gradient-to-r from-primary-400/15 via-blue-500/15 to-primary-400/15 rounded-3xl blur-xl"
                  variants={glowVariants}
                  initial="initial"
                />
                
                <div className="relative z-10">
                  <AnimatePresence mode="wait">
                    {loading ? (
                      <LoadingSpinner message="Loading advisors..." bgColor="bg-transparent" spinnerColor="border-[#73ab40]" py={24} />
                    ) : error ? (
                      <motion.div 
                        key="error"
                        className="text-center py-32"
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.6, type: "spring" }}
                      >
                        <motion.div
                          className="relative w-24 h-24 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm"
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <span className="text-4xl">✨</span>
                        </motion.div>
                        
                        <h3 className="text-blue-600 text-2xl mb-6 font-bold">Advisors will be available soon. Check back later!</h3>
                        
                        <motion.button 
                          onClick={() => window.location.reload()} 
                          className="group relative px-10 py-4 bg-gradient-to-r from-blue-600 to-primary-500 text-white rounded-2xl font-semibold transition-all duration-300 overflow-hidden"
                          whileHover={{ 
                            scale: 1.05,
                            boxShadow: "0 20px 40px rgba(201, 166, 53, 0.3)"
                          }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-primary-500 to-blue-600"
                            initial={{ x: "-100%" }}
                            whileHover={{ x: "0%" }}
                            transition={{ duration: 0.3 }}
                          />
                          <span className="relative z-10">Try Again</span>
                        </motion.button>
                      </motion.div>
                    ) : filteredAdvisors.length === 0 ? (
                      <motion.div 
                        key="empty"
                        className="text-center py-32"
                        initial={{ opacity: 0, y: 50 }}
                        exit={{ opacity: 0, y: -50 }}
                        transition={{ duration: 0.6 }}
                      >
                        <motion.div
                          className="w-32 h-32 bg-gradient-to-br from-gray-400/20 to-slate-400/20 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm relative overflow-hidden"
                          transition={{ 
                            duration: 0.4, 
                            ease: "easeInOut" 
                          }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-full"

                          />
                          <span className="text-5xl relative z-10">🔍</span>
                        </motion.div>
                        
                        <motion.h3
                          className="text-gray-300 text-2xl mb-4 font-semibold"
                        >
                          {searchTerm ? `No advisors found for "${searchTerm}"` : "No advisors available yet"}
                        </motion.h3>
                        
                        <motion.p
                          className="text-gray-400 text-lg"
                        >
                          {searchTerm ? "Try adjusting your search terms" : "We're constantly expanding our advisory board"}
                        </motion.p>
                      </motion.div>
                    ) : (
                      <>
                        {/* Top Advisors Section */}
                        {topAdvisors.length > 0 && (
                          <div className="mb-16">
                            {/* Top Advisors Title */}
                            <motion.h2 
                              className="text-3xl sm:text-4xl font-bold mb-10 text-center"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}
                              style={{ color: '#3a8b9e' }}
                            >
                              Top Advisors
                            </motion.h2>

                            {/* Top Advisors Grid */}
                            <motion.div
                              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
                              variants={containerVariants}
                            >
                              {topAdvisors.map((advisor, idx) => {
                                const imgUrl = resolvePhotoUrl(advisor, API_BASE_URL, cacheBustTimestamp.current);

                                return (
                                  <Link 
                                    key={advisor.id || idx} 
                                    to={`/advisors/${advisor.id}`} 
                                    className="block h-full group"
                                  >
                                    <motion.div
                                      className="group relative"
                                      variants={cardVariants}
                                      whileHover="hover"
                                    >
                                      {/* Main Card */}
                                      <motion.div
                                        className="relative h-full p-8 bg-white/10 backdrop-blur-xl rounded-3xl border border-gold/50 hover:border-gold/80 shadow-xl cursor-pointer overflow-hidden flex flex-col items-center justify-between min-h-[340px] max-h-[340px]"
                                      >
                                        {/* Advisor Avatar */}
                                        <motion.div
                                          className="mb-8 relative"
                                          whileHover={{ scale: 1.1, rotate: 5 }}
                                          transition={{ type: "spring", stiffness: 300 }}
                                        >
                                          {(advisor.photo_url && validImages[advisor.id] !== false) ? (
                                            <motion.div
                                              className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-gold/60 shadow-2xl"
                                              whileHover={{ borderColor: "rgba(201, 166, 53, 0.9)" }}
                                            >
                                              <img
                                                src={imgUrl}
                                                alt={advisor.name}
                                                className="w-full h-full object-cover"
                                                onError={() => {
                                                  setValidImages(prev => ({ ...prev, [advisor.id]: false }));
                                                }}
                                                onLoad={() => {
                                                  setValidImages(prev => ({ ...prev, [advisor.id]: true }));
                                                }}
                                              />
                                              <motion.div
                                                className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent"

                                              />
                                            </motion.div>
                                          ) : (
                                            <motion.div
                                              className="w-24 h-24 bg-gradient-to-br from-yellow-400 via-gold to-yellow-500 rounded-full flex items-center justify-center shadow-strong relative overflow-hidden"
                                              whileHover={{ boxShadow: "0 0 40px rgba(201, 166, 53, 0.8)" }}
                                            >
                                              <motion.div
                                                className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent rounded-full"
                                              />
                                              <span className="text-2xl font-bold text-slate-900 select-none relative z-10">
                                                {getInitials(advisor.name)}
                                              </span>
                                            </motion.div>
                                          )}
                                          
                                          {/* Avatar Glow Ring */}
                                          <motion.div
                                            className="absolute inset-0 rounded-full border border-gold/60"
                                            animate={{ 
                                              scale: [1, 1.2, 1], 
                                              opacity: [0.5, 0, 0.5] 
                                            }}
                                            transition={{ 
                                              duration: 3, 
                                              repeat: Infinity, 
                                              delay: idx * 0.3 
                                            }}
                                          />
                                        </motion.div>
                                        
                                        {/* Advisor Name */}
                                        <motion.div
                                          className="flex-1 flex items-center text-center"
                                          initial={{ opacity: 0.8 }}
                                          whileHover={{ opacity: 1 }}
                                        >
                                          <h3 className="text-black font-bold text-xl leading-tight break-words">
                                            {advisor.name}
                                          </h3>
                                        </motion.div>
                                        
                                        {/* Enhanced Badge */}
                                        <motion.div
                                          className="mt-6 relative group/badge"
                                          whileHover={{ scale: 1.05 }}
                                        >
                                          <motion.div
                                            className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full blur opacity-30"
                                            animate={{ 
                                              opacity: hoveredCard === `top-${idx}` ? 0.6 : 0.3 
                                            }}
                                          />
                                          <span className="relative inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-emerald-500/80 backdrop-blur-sm text-white text-xs sm:text-sm font-semibold rounded-full border border-emerald-400/40 transition-all duration-300 whitespace-nowrap">
                                            <span className="mr-2">⭐</span>
                                            Top Advisor
                                          </span>
                                        </motion.div>
                                        
                                        {/* Hover Overlay */}
                                        <motion.div
                                          className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100"
                                          transition={{ duration: 0.3 }}
                                        />
                                      </motion.div>
                                    </motion.div>
                                  </Link>
                                );
                              })}
                            </motion.div>
                          </div>
                        )}

                        {/* Regular Advisors by Area */}
                        {Object.entries(groupedAdvisors).map(([areaOfFocus, advisorsInArea], sectionIdx) => (
                          <div key={areaOfFocus} className="mb-16 last:mb-0">
                            {/* Area of Focus Title */}
                            <motion.h2 
                              className="text-3xl sm:text-4xl font-bold mb-10 text-center"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.3 }}
                              style={{ color: '#3a8b9e' }}
                            >
                              {areaOfFocus}
                            </motion.h2>

                            {/* Advisors Grid */}
                            <motion.div
                              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
                              variants={containerVariants}
                            >
                              {advisorsInArea.map((advisor, idx) => {
                                const globalIdx = sectionIdx * 10 + idx;
                                const imgUrl = resolvePhotoUrl(advisor, API_BASE_URL, cacheBustTimestamp.current);

                                return (
                                  <Link 
                                    key={advisor.id || idx} 
                                    to={`/advisors/${advisor.id}`} 
                                    className="block h-full group"
                                  >
                                    <motion.div
                                      className="group relative"
                                      variants={cardVariants}
                                      whileHover="hover"
                                    >
                                      {/* Main Card */}
                                      <motion.div
                                        className={`relative h-full p-8 backdrop-blur-xl rounded-3xl shadow-xl cursor-pointer overflow-hidden flex flex-col items-center justify-between min-h-[340px] max-h-[340px] ${
                                          advisor.is_top_advisor === 1 || advisor.is_top_advisor === true
                                            ? "bg-white/10 border border-emerald-400/50 hover:border-emerald-400/80"
                                            : "bg-white/10 border border-gold/30 hover:border-gold/50"
                                        }`}
                                      >
                                        
                                        {/* Advisor Avatar */}
                                        <motion.div
                                          className="mb-8 relative"
                                          whileHover={{ scale: 1.1, rotate: 5 }}
                                          transition={{ type: "spring", stiffness: 300 }}
                                        >
                                          {(advisor.photo_url && validImages[advisor.id] !== false) ? (
                                            <motion.div
                                              className={`relative w-24 h-24 rounded-full overflow-hidden border-4 shadow-2xl ${
                                                advisor.is_top_advisor === 1 || advisor.is_top_advisor === true
                                                  ? "border-emerald-400/60"
                                                  : "border-gold/40"
                                              }`}
                                              whileHover={{ borderColor: advisor.is_top_advisor === 1 || advisor.is_top_advisor === true ? "rgba(16, 185, 129, 0.9)" : "rgba(201, 166, 53, 0.7)" }}
                                            >
                                              <img
                                                src={imgUrl}
                                                alt={advisor.name}
                                                className="w-full h-full object-cover"
                                                onError={() => {
                                                  setValidImages(prev => ({ ...prev, [advisor.id]: false }));
                                                }}
                                                onLoad={() => {
                                                  setValidImages(prev => ({ ...prev, [advisor.id]: true }));
                                                }}
                                              />
                                              <motion.div
                                                className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent"
                                              />
                                            </motion.div>
                                          ) : (
                                            <motion.div
                                              className="w-24 h-24 bg-gradient-to-br from-primary-500 via-emerald-700 to-primary-600 rounded-full flex items-center justify-center shadow-strong relative overflow-hidden"
                                              whileHover={{ boxShadow: "0 0 40px rgba(66, 149, 189, 0.6)" }}
                                            >
                                              <motion.div
                                                className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent rounded-full"
                                              />
                                              <span className="text-2xl font-bold text-white select-none relative z-10">
                                                {getInitials(advisor.name)}
                                              </span>
                                            </motion.div>
                                          )}
                                          
                                          {/* Avatar Glow Ring */}
                                          <motion.div
                                            className={`absolute inset-0 rounded-full border ${
                                              advisor.is_top_advisor === 1 || advisor.is_top_advisor === true
                                                ? "border-emerald-400/60"
                                                : "border-gold/40"
                                            }`}
                                          />
                                        </motion.div>
                                        
                                        {/* Advisor Name */}
                                        <motion.div
                                          className="flex-1 flex items-center text-center"
                                          whileHover={{ opacity: 1 }}
                                        >
                                          <h3 className="text-black font-bold text-xl leading-tight break-words">
                                            {advisor.name}
                                          </h3>
                                        </motion.div>
                                        
                                        {/* Enhanced Badge */}
                                        <motion.div
                                          className="mt-6 relative group/badge"
                                        >
                                          <motion.div
                                            className={`absolute -inset-1 rounded-full blur ${
                                              advisor.is_top_advisor === 1 || advisor.is_top_advisor === true
                                                ? "bg-gradient-to-r from-emerald-500 to-emerald-400 opacity-30"
                                                : "bg-gradient-to-r from-emerald-700 to-primary-500 opacity-30"
                                            }`}
                                            animate={{ 
                                              opacity: hoveredCard === globalIdx ? 0.6 : (advisor.is_top_advisor === 1 || advisor.is_top_advisor === true ? 0.3 : 0.3)
                                            }}
                                          />
                                          <span className={`relative inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm font-semibold rounded-full border transition-all duration-300 whitespace-nowrap ${
                                            advisor.is_top_advisor === 1 || advisor.is_top_advisor === true
                                              ? "bg-emerald-500/80 backdrop-blur-sm text-white border-emerald-400/40"
                                              : "bg-white/60 backdrop-blur-sm text-slate-900 border-primary-300/40"
                                          }`}>
                                            {advisor.is_top_advisor === 1 || advisor.is_top_advisor === true ? (
                                              <>
                                                <span className="mr-2">⭐</span>
                                                Top Advisor
                                              </>
                                            ) : (
                                              "✨ Expert Advisor"
                                            )}
                                          </span>
                                        </motion.div>
                                        
                                        {/* Hover Overlay */}
                                        <motion.div
                                          className="absolute inset-0 bg-gradient-to-br from-emerald-400/10 to-transparent rounded-3xl opacity-0 group-hover:opacity-100"
                                          transition={{ duration: 0.3 }}
                                        />
                                      </motion.div>
                                    </motion.div>
                                  </Link>
                                );
                              })}
                            </motion.div>
                          </div>
                        ))}

                        {/* Regular Advisors by Area */}
                      </>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>

            {/* Bottom Section */}
            <motion.div 
              className="text-center mt-20"
            >
              <motion.div
                className="flex justify-center items-center"
              >
              </motion.div> 
              
              {/* Footer Call to Action */}
              <motion.div
                className="relative "
              >
                <motion.div/>
                <p className="relative text-xl font-light tracking-wide">
                  <motion.span>
                    Guided by wisdom, driven by excellence
                  </motion.span>
                </p>
              </motion.div>
              
              
            </motion.div>
          </div>
        </div>

        {/* Custom Styles */}
        <style>{`
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
          
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          
          .animate-shimmer {
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            background-size: 200% 100%;
            animation: shimmer 3s infinite;
          }
        `}</style>
      </div>
    </div>
  );
}

export default AdvisorsPage;